
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import {
  Lead, PermissionCode, User,
} from '@/types';
import UTILS from '@/lib/UTILS';
import API from '@/lib/API';
import DatePickerDialog from '@/components/DatePickerDialog.vue';

@Component({
  components: {
    DatePickerDialog,
  },
})
export default class EditLeadPaymentsDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;
  @Prop(Number) readonly projectId!: number;

  dialog = false
  valid = false
  loading = false

  paymentsNotesUpdatedAt: Date | null = null
  paymentsNotesUpdatedBy: string | null = null

  datePickerDialog = false
  datePickerDate: string | null = null
  datePickerName: string | null = null

  isUpdatingFromWatcher = false

  update = {
    payments: 0,
    paymentsNotes: '',
    firstPaymentDate: null,
    secondPaymentDate: null,
    partialInstallationPaymentDate: null,
    finalPaymentDate: null,
    latestPaymentDate: null,
    firstPaymentInvoiceRef: null,
    firstPaymentInvoiceDate: null,
    secondPaymentInvoiceRef: null,
    secondPaymentInvoiceDate: null,
    partialInstallationPaymentInvoiceRef: null,
    partialInstallationPaymentInvoiceDate: null,
    finalPaymentInvoiceRef: null,
    finalPaymentInvoiceDate: null,
    invoiceDiscount: null,
    loan: null,
  } as {
    payments: number;
    paymentsNotes: string;
    firstPaymentDate: Date | null;
    secondPaymentDate: Date | null;
    partialInstallationPaymentDate: Date | null;
    finalPaymentDate: Date | null;
    latestPaymentDate: Date | null;
    firstPaymentInvoiceRef: string | null;
    firstPaymentInvoiceDate: Date | null;
    secondPaymentInvoiceRef: string | null;
    secondPaymentInvoiceDate: Date | null;
    partialInstallationPaymentInvoiceRef: string | null;
    partialInstallationPaymentInvoiceDate: Date | null;
    finalPaymentInvoiceRef: string | null;
    finalPaymentInvoiceDate: Date | null;
    invoiceDiscount: string | null;
    loan: string | null;
  }

  @Watch('value')
  async onValueChange(val: boolean) {
    if (val === true) {
      if (this.projectId) {
        await this.$store.dispatch('selectLead', { id: this.projectId });
      }

      // Carica tutti i valori
      this.$set(this.update, 'invoiceDiscount', this.getCustomValueTextByName('invoiceDiscount'));
      this.$set(this.update, 'loan', this.getCustomValueTextByName('loan'));
      this.$set(this.update, 'paymentsNotes', this.getCustomValueTextByName('paymentsNotes') || '');
      this.$set(this.update, 'firstPaymentDate', this.getCustomValueDateByName('firstPaymentDate'));
      this.$set(this.update, 'secondPaymentDate', this.getCustomValueDateByName('secondPaymentDate'));
      this.$set(this.update, 'partialInstallationPaymentDate', this.getCustomValueDateByName('partialInstallationPaymentDate'));
      this.$set(this.update, 'finalPaymentDate', this.getCustomValueDateByName('finalPaymentDate'));
      this.$set(this.update, 'latestPaymentDate', this.getCustomValueDateByName('latestPaymentDate'));
      this.$set(this.update, 'firstPaymentInvoiceRef', this.getCustomValueTextByName('firstPaymentInvoiceRef'));
      this.$set(this.update, 'firstPaymentInvoiceDate', this.getCustomValueDateByName('firstPaymentInvoiceDate'));
      this.$set(this.update, 'secondPaymentInvoiceRef', this.getCustomValueTextByName('secondPaymentInvoiceRef'));
      this.$set(this.update, 'secondPaymentInvoiceDate', this.getCustomValueDateByName('secondPaymentInvoiceDate'));
      this.$set(this.update, 'partialInstallationPaymentInvoiceRef', this.getCustomValueTextByName('partialInstallationPaymentInvoiceRef'));
      this.$set(this.update, 'partialInstallationPaymentInvoiceDate', this.getCustomValueDateByName('partialInstallationPaymentInvoiceDate'));
      this.$set(this.update, 'finalPaymentInvoiceRef', this.getCustomValueTextByName('finalPaymentInvoiceRef'));
      this.$set(this.update, 'finalPaymentInvoiceDate', this.getCustomValueDateByName('finalPaymentInvoiceDate'));
      this.paymentsNotesUpdatedAt = this.getCustomValueDateByName('paymentsNotesUpdatedAt');

      // Calcola il livello basato sulle date esistenti
      const computedLevel = this.computedPaymentLevel;
      const savedPayments = this.lead.payments;

      // Se il livello calcolato è diverso da quello salvato, usa il livello calcolato (tranne se payments = -1)
      if (savedPayments !== -1 && computedLevel !== savedPayments) {
        this.$set(this.update, 'payments', computedLevel);
        // Aggiorna latestPaymentDate se necessario
        if (computedLevel > 0) {
          const paymentKeys = ['firstPaymentDate', 'secondPaymentDate', 'partialInstallationPaymentDate', 'finalPaymentDate'];
          const latestPaymentKey = paymentKeys[computedLevel - 1];
          this.$set(this.update, 'latestPaymentDate', this.update[latestPaymentKey]);
        }
      } else {
        this.$set(this.update, 'payments', savedPayments);
      }

      this.dialog = val;

      this.paymentsNotesUpdatedBy = await this.getUserNameFromCustomValue('paymentsNotesUpdatedBy');
    } else {
      this.dialog = val;
    }
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  @Watch('update.firstPaymentDate')
  onFirstPaymentDateChange() {
    if (this.isUpdatingFromWatcher) return;
    this.updatePaymentLevelFromDates();
  }

  @Watch('update.secondPaymentDate')
  onSecondPaymentDateChange() {
    if (this.isUpdatingFromWatcher) return;
    this.updatePaymentLevelFromDates();
  }

  @Watch('update.partialInstallationPaymentDate')
  onPartialInstallationPaymentDateChange() {
    if (this.isUpdatingFromWatcher) return;
    this.updatePaymentLevelFromDates();
  }

  @Watch('update.finalPaymentDate')
  onFinalPaymentDateChange() {
    if (this.isUpdatingFromWatcher) return;
    this.updatePaymentLevelFromDates();
  }

  updatePaymentLevelFromDates() {
    if (this.update.payments === -1) {
      // Non aggiornare automaticamente se payments è -1 (nessun pagamento dovuto)
      return;
    }

    const newLevel = this.computedPaymentLevel;

    // Aggiorna latestPaymentDate con la data dell'ultimo pagamento con data inserita
    const paymentKeys = ['firstPaymentDate', 'secondPaymentDate', 'partialInstallationPaymentDate', 'finalPaymentDate'];
    if (newLevel > 0) {
      const latestPaymentKey = paymentKeys[newLevel - 1];
      this.isUpdatingFromWatcher = true;
      this.$set(this.update, 'latestPaymentDate', this.update[latestPaymentKey]);
      this.$set(this.update, 'payments', newLevel);
      this.$nextTick(() => {
        this.isUpdatingFromWatcher = false;
      });
    } else {
      // Se non ci sono date, torna a 0 (nessun pagamento effettuato)
      this.isUpdatingFromWatcher = true;
      this.$set(this.update, 'latestPaymentDate', null);
      this.$set(this.update, 'payments', 0);
      this.$nextTick(() => {
        this.isUpdatingFromWatcher = false;
      });
    }
  }

  onPaymentsChange(val: number) {
    console.log('EditLeadPaymentsDialog: onPaymentsChange()'); // eslint-disable-line no-console

    // I pagamenti 1-4 non sono più selezionabili manualmente, gestiamo solo -1 e 0
    if (val > 0) {
      // Se per qualche motivo viene chiamato con val > 0, non fare nulla
      // Il livello viene gestito automaticamente dai watchers
      return;
    }

    // Evita loop infiniti con i watchers
    this.isUpdatingFromWatcher = true;

    // Array delle chiavi dei pagamenti (custom values)
    const paymentKeys = ['firstPaymentDate', 'secondPaymentDate', 'partialInstallationPaymentDate', 'finalPaymentDate'];

    if (val === -1) {
      // Reset completo: tutti i campi (date pagamenti, riferimenti fattura, date fattura, note)
      paymentKeys.forEach((key) => {
        this.$set(this.update, key, null);
      });
      this.$set(this.update, 'latestPaymentDate', null);
      this.$set(this.update, 'firstPaymentInvoiceRef', null);
      this.$set(this.update, 'firstPaymentInvoiceDate', null);
      this.$set(this.update, 'secondPaymentInvoiceRef', null);
      this.$set(this.update, 'secondPaymentInvoiceDate', null);
      this.$set(this.update, 'partialInstallationPaymentInvoiceRef', null);
      this.$set(this.update, 'partialInstallationPaymentInvoiceDate', null);
      this.$set(this.update, 'finalPaymentInvoiceRef', null);
      this.$set(this.update, 'finalPaymentInvoiceDate', null);
      this.$set(this.update, 'paymentsNotes', '');
    } else if (val === 0) {
      // Reset solo date pagamenti, mantiene riferimenti fattura e date fattura
      paymentKeys.forEach((key) => {
        this.$set(this.update, key, null);
      });
      this.$set(this.update, 'latestPaymentDate', null);
    }

    this.$nextTick(() => {
      this.isUpdatingFromWatcher = false;
      if (this.form) this.form.validate();
    });
  }

  get payments(): number {
    return this.update.payments;
  }

  get computedPaymentLevel(): number {
    // Calcola il livello di pagamento basato sulle date inserite
    // Priorità: 4 > 3 > 2 > 1
    if (this.update.finalPaymentDate) return 4;
    if (this.update.partialInstallationPaymentDate) return 3;
    if (this.update.secondPaymentDate) return 2;
    if (this.update.firstPaymentDate) return 1;
    return 0;
  }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get userPermissions(): PermissionCode[] {
    return this.$store.getters.currentUserPermissions;
  }

  get lead(): Lead {
    return this.$store.getters.currentLead;
  }

  get form(): Vue & { validate: () => boolean; reset: () => boolean } {
    return this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };
  }

  hasPermission(codename: PermissionCode) {
    if (!this.user) return false;
    if (this.user.isSuperUser) return true;

    return this.userPermissions.includes(codename);
  }

  mDate(date: Date) {
    return moment(date).format('L');
  }

  updatedAtLong(updatedAt: Date | null, updatedBy: string | null) {
    return updatedAt && updatedBy
      ? `Ultima modifica: ${moment(updatedAt).format('L')} ore ${moment(updatedAt).format('LT')} `
        + `(${updatedBy ?? 'Sconosciuto'})`
      : '';
  }

  getCustomValueTextByName(name: string): string | null {
    return UTILS.getCustomValueTextByName(this.lead, name) ?? null;
  }

  getCustomValueDateByName(name: string): Date | null {
    return UTILS.getCustomValueDateByName(this.lead, name) || null;
  }

  async getUserNameFromCustomValue(name: string): Promise<string | null> {
    const userId = UTILS.getCustomValueUserIdByName(this.lead, name) || null;
    const userName = userId ? await API.getUserName(userId) : null;
    return userName;
  }

  onClickDate(name: string) {
    console.log('EditLeadPaymentsDialog: onClickDate()'); // eslint-disable-line no-console

    this.datePickerName = name;

    // Imposta l'eventuale data precedentemente selezionata
    const dateValue = name in this.update ? this.update[name] : null;
    this.datePickerDate = dateValue
      ? moment(dateValue).toISOString(true).substring(0, 10)
      : null;

    this.datePickerDialog = true;
  }

  async onClearDate(name: string) {
    console.log('EditLeadPaymentsDialog: onClearDate()'); // eslint-disable-line no-console

    // Usa $set per assicurare che i watchers si attivino
    switch (name) {
      case 'firstPaymentDate':
        this.$set(this.update, 'firstPaymentDate', null);
        // Il watcher aggiornerà automaticamente il livello
        break;
      case 'secondPaymentDate':
        this.$set(this.update, 'secondPaymentDate', null);
        // Il watcher aggiornerà automaticamente il livello
        break;
      case 'partialInstallationPaymentDate':
        this.$set(this.update, 'partialInstallationPaymentDate', null);
        // Il watcher aggiornerà automaticamente il livello
        break;
      case 'finalPaymentDate':
        this.$set(this.update, 'finalPaymentDate', null);
        // Il watcher aggiornerà automaticamente il livello
        break;
      case 'latestPaymentDate':
        // latestPaymentDate viene aggiornato automaticamente dai watchers
        // Non dovrebbe essere modificato manualmente
        break;
      case 'firstPaymentInvoiceDate':
        this.$set(this.update, 'firstPaymentInvoiceDate', null);
        break;
      case 'secondPaymentInvoiceDate':
        this.$set(this.update, 'secondPaymentInvoiceDate', null);
        break;
      case 'partialInstallationPaymentInvoiceDate':
        this.$set(this.update, 'partialInstallationPaymentInvoiceDate', null);
        break;
      case 'finalPaymentInvoiceDate':
        this.$set(this.update, 'finalPaymentInvoiceDate', null);
        break;

      default:
        break;
    }
  }

  onSelectDate(date: string | null) {
    console.log(`${this.constructor.name}: onSelectDate() date =`, date); // eslint-disable-line no-console

    if (!this.datePickerName) return;

    // Chiudo il dialog per la selezione della data
    this.datePickerDialog = false;

    // Trasforma in un valore di tipo Date prima di restituirlo
    const selectedDate = date
      ? moment(date, 'YYYY-MM-DD').toDate()
      : null;

    // Inserisco la data nell'aggiornamento
    if (this.datePickerName in this.update) {
      this.$set(this.update, this.datePickerName, selectedDate);

      // Se è una data di pagamento (non fattura), il watcher aggiornerà automaticamente il livello
      const paymentDateKeys = ['firstPaymentDate', 'secondPaymentDate', 'partialInstallationPaymentDate', 'finalPaymentDate'];
      if (paymentDateKeys.includes(this.datePickerName)) {
        // Il watcher si occuperà di aggiornare il livello e latestPaymentDate
        console.log(`${this.constructor.name}: onSelectDate() payment date selected, watcher will update level`); // eslint-disable-line no-console
      } else if (this.datePickerName === 'latestPaymentDate') {
        // latestPaymentDate viene aggiornato automaticamente dai watchers, non dovrebbe essere modificato manualmente
        // Ma se viene modificato, non facciamo nulla
      }
    }

    this.$nextTick(() => {
      if (this.form) this.form.validate();
    });
  }

  onCancel() {
    this.dialog = false;
  }

  async onUpdate() {
    console.log('EditLeadPaymentsDialog: onUpdate()'); // eslint-disable-line no-console

    const form = 'form' in this.$refs ? this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    } : null;

    if (form?.validate()) {
      this.loading = true;

      await this.$store.dispatch('updateLead', {
        updateType: 'payments',
        update: this.update,
      });

      this.dialog = false;
      setTimeout(() => {
        this.loading = false;
      }, 500);
    }
  }
}
